import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Accordion, Card, useAccordionToggle } from "react-bootstrap";
import { useEffect, useState } from "react";

function SidebarItem({
  nodes,
  eventKeyPrefix = "",
  onSelect,
  selectedCategoryId,
  selectedSubCategoryId,
  depth = 0
}) {
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined);

  // Helper function — recursively checks if a node or its descendants match the ID
  const findInTree = (node, targetId) => {
    if (node.id === targetId) return true;

    return node.sub_categories?.some((sub) => findInTree(sub, targetId)) ?? false;
  }

  // Automatically expand the parent if it contains the selected subcategory
  useEffect(() => {
    const targetId = selectedCategoryId || selectedSubCategoryId;

    if (!nodes || !targetId) {
      setActiveKey(undefined);

      return;
    };

    const parentNode = nodes.find((node) => 
      node.sub_categories?.length > 0 && findInTree(node, targetId)
    );

    if (parentNode) {
      setActiveKey(eventKeyPrefix + parentNode.id);
    }
  }, [nodes, selectedCategoryId, selectedSubCategoryId, eventKeyPrefix]);

  if (!nodes || nodes.length === 0) return null;

  const CustomToggle = ({ eventKey, node, isExpanded, isSelected, hasSubCategories }) => {
    const decoratedOnClick = useAccordionToggle(eventKey, () => {
      setActiveKey(isExpanded ? undefined : String(eventKey));
    });

    return (
      <Card.Header
        className={`sidebar-category-header ${isSelected ? "selected" : ""} ${depth > 0 ? "sidebar-subcategory" : ""}`}
        style={{ paddingLeft: depth > 0 ? `${20 + (depth * 15)}px` : undefined }}
        onClick={(e) => {
          onSelect(node);
          if (hasSubCategories) {
            decoratedOnClick(e);
          } else {
            setActiveKey(undefined);
          }
        }}
      >
        <span className="category-name">{node.name}</span>
        {hasSubCategories && (
          <FontAwesomeIcon
            icon={isExpanded ? faChevronUp : faChevronDown}
            className="chevron-icon"
          />
        )}
      </Card.Header>
    );
  };

  return (
    <Accordion activeKey={activeKey}>
      {nodes.map((node) => {
        const eventKey = eventKeyPrefix + node.id;
        const isExpanded = activeKey === String(eventKey);
        const isSelected = selectedSubCategoryId ? selectedSubCategoryId === node.id : selectedCategoryId === node.id;
        const hasSubCategories = node.sub_categories && node.sub_categories.length > 0;

        return (
          <Card key={eventKey} className="sidebar-card">
            <CustomToggle 
              eventKey={String(eventKey)} 
              node={node}
              isExpanded={isExpanded}
              isSelected={isSelected}
              hasSubCategories={hasSubCategories}
            />

            {hasSubCategories && (
              <Accordion.Collapse eventKey={String(eventKey)}>
                <Card.Body className="p-0">
                  <SidebarItem
                    nodes={node.sub_categories}
                    eventKeyPrefix={eventKey + "-"}
                    onSelect={onSelect}
                    selectedCategoryId={selectedCategoryId}
                    selectedSubCategoryId={selectedSubCategoryId}
                    depth={depth + 1}
                  />
                </Card.Body>
              </Accordion.Collapse>
            )}
          </Card>
        );
      })}
    </Accordion>
  );
}

export default SidebarItem;